import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Images, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { usePhotoUpload } from '../climbs/climb-editor/usePhotoUpload';
import { competitionRoundsService } from '../../services/competition-rounds';
import type { RoundClimb } from '../../types/competition-round';

interface RoundClimbPhotoButtonProps {
  competitionSlug: string;
  roundId: string;
  roundClimb: RoundClimb;
  /** The round climb as the server sends it back, photo attached. */
  onUploaded: (roundClimb: RoundClimb) => void;
}

/**
 * A camera icon beside a block's name: the way to give it its picture, there
 * and then, from the phone in hand. It asks first — the camera or the gallery —
 * because a block is set the night before and photographed whenever somebody
 * gets to it, and either may be the one that has the shot.
 *
 * The photo goes up as a direct upload and is then hung on the round climb
 * itself (RoundClimb#image): a numbered block has no Climb record to give a
 * picture to, and creating one for the purpose is the bookkeeping the blocks
 * were made to avoid.
 *
 * Every click inside — the dialog's included, which React bubbles through the
 * portal — stops here: the button sits on rows that open the scoring page.
 */
export function RoundClimbPhotoButton({
  competitionSlug,
  roundId,
  roundClimb,
  onUploaded,
}: RoundClimbPhotoButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { upload } = usePhotoUpload();
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const attach = async (file: File | undefined) => {
    if (!file || saving) return;

    setOpen(false);
    setSaving(true);
    try {
      const signedId = await upload(file);
      const updated = await competitionRoundsService.updateRoundClimb(
        competitionSlug,
        roundId,
        roundClimb.id,
        { image_signed_id: signedId }
      );
      onUploaded(updated);
      toast({ title: t('competitionRounds.photoAttached') });
    } catch (error) {
      toast({
        title: t('errors.actionFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const pick = (event: React.ChangeEvent<HTMLInputElement>) => {
    attach(event.target.files?.[0]);
    // So the same file can be chosen again after a failed try.
    event.target.value = '';
  };

  return (
    <span className="contents" onClick={(event) => event.stopPropagation()}>
      {/* Outside the dialog, which closes as soon as a file is picked: an input
          inside it would be gone before its change event arrived. */}
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="round-climb-photo-camera"
        onChange={pick}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="round-climb-photo-gallery"
        onChange={pick}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 shrink-0 text-muted-foreground"
        aria-label={t('competitionRounds.setPhoto')}
        disabled={saving}
        onClick={() => setOpen(true)}
        data-testid="round-climb-photo-button"
      >
        {saving ? <Loader2 className="size-4 animate-spin" /> : <Camera className="size-4" />}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('competitionRounds.setPhoto')} — {roundClimb.display_name}
            </DialogTitle>
            <DialogDescription>{t('competitionRounds.setPhotoDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Button
              type="button"
              variant="outline"
              className="w-full py-6 text-base"
              onClick={() => cameraInput.current?.click()}
            >
              <Camera className="mr-2 size-5" />
              {t('competitionRounds.takePhoto')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full py-6 text-base"
              onClick={() => galleryInput.current?.click()}
            >
              <Images className="mr-2 size-5" />
              {t('competitionRounds.uploadPhoto')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
}
